/**
 * CMS content API client.
 *
 * Contract-first: these methods call the FT backend CMS endpoints documented
 * in `docs/cms-content-model.md`. Because that backend is a SEPARATE service
 * that hasn't shipped the endpoints yet, the client can serve `cmsMocks`
 * instead, controlled EXPLICITLY by the `REACT_APP_CMS_USE_MOCKS` env flag
 * (defaults ON until the backend is live — this is not a silent catch-all
 * fallback; when mocks are OFF, live errors surface to the caller).
 *
 * All methods return the domain payload directly (unwrapped from the FT
 * `{ data: { data } }` envelope) so components and tests consume a single shape.
 */
import ftApiClient from "./ftApiClient";
import { cmsMocks } from "./cmsMocks";
import {
    CmsFlavor,
    CmsHomepage,
    CmsMenu,
    CmsMenuLocation,
    CmsPage,
    CmsPost,
    CmsPostList,
    CmsTemplate,
} from "@interfaces";

/** Whether to serve mock content. Defaults ON until the FT CMS backend is live. */
export const CMS_USE_MOCKS = process.env.REACT_APP_CMS_USE_MOCKS !== "false";

/** The active brand flavor, from the build-time env. */
export const getCmsFlavor = (): CmsFlavor | undefined => {
    const flavor = process.env.REACT_APP_FLAVOR;
    return flavor === "peterpans" || flavor === "freelance" ? flavor : undefined;
};

type ReadOpts = { preview?: boolean; flavor?: CmsFlavor };

/** Unwrap the FT `{ data: { data } }` envelope, tolerating a single-level shape. */
const unwrap = <T>(res: any): T => {
    const body = res?.data;
    if (body && typeof body === "object" && "data" in body) return body.data as T;
    return body as T;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
    const entries = Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== "",
    );
    if (!entries.length) return "";
    const qs = entries
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&");
    return `?${qs}`;
};

export const cmsApi = {
    /** Reusable page templates / allowed section blocks (authoring metadata). */
    getTemplates: async (): Promise<CmsTemplate[]> => {
        if (CMS_USE_MOCKS) return cmsMocks.getTemplates();
        const res = await ftApiClient.get("/cms/templates");
        return unwrap<CmsTemplate[]>(res) ?? [];
    },

    /** Backend-composed homepage content (hero + ordered sections). */
    getHomepage: async (opts?: ReadOpts): Promise<CmsHomepage> => {
        const flavor = opts?.flavor ?? getCmsFlavor();
        if (CMS_USE_MOCKS) return cmsMocks.getHomepage(flavor);
        const query = buildQuery({ flavor, preview: opts?.preview });
        const res = await ftApiClient.get(`/cms/homepage${query}`);
        return unwrap<CmsHomepage>(res);
    },

    /** A single standalone page by slug. Returns null when not found/unpublished. */
    getPageBySlug: async (slug: string, opts?: ReadOpts): Promise<CmsPage | null> => {
        const flavor = opts?.flavor ?? getCmsFlavor();
        if (CMS_USE_MOCKS) return cmsMocks.getPageBySlug(slug, { preview: opts?.preview, flavor });
        const query = buildQuery({ flavor, preview: opts?.preview });
        try {
            const res = await ftApiClient.get(`/cms/pages/${encodeURIComponent(slug)}${query}`);
            return unwrap<CmsPage>(res) ?? null;
        } catch (err: any) {
            if (err?.response?.status === 404) return null;
            throw err;
        }
    },

    /** Paginated list of blog posts (published only, unless preview). */
    listPosts: async (
        opts?: ReadOpts & { page?: number; perPage?: number; tag?: string },
    ): Promise<CmsPostList> => {
        const flavor = opts?.flavor ?? getCmsFlavor();
        if (CMS_USE_MOCKS)
            return cmsMocks.listPosts({
                page: opts?.page,
                perPage: opts?.perPage,
                tag: opts?.tag,
                preview: opts?.preview,
                flavor,
            });
        const query = buildQuery({
            flavor,
            preview: opts?.preview,
            page: opts?.page,
            per_page: opts?.perPage,
            tag: opts?.tag,
        });
        const res = await ftApiClient.get(`/cms/posts${query}`);
        return unwrap<CmsPostList>(res);
    },

    /** A single blog post by slug. Returns null when not found/unpublished. */
    getPostBySlug: async (slug: string, opts?: ReadOpts): Promise<CmsPost | null> => {
        const flavor = opts?.flavor ?? getCmsFlavor();
        if (CMS_USE_MOCKS) return cmsMocks.getPostBySlug(slug, { preview: opts?.preview, flavor });
        const query = buildQuery({ flavor, preview: opts?.preview });
        try {
            const res = await ftApiClient.get(`/cms/posts/${encodeURIComponent(slug)}${query}`);
            return unwrap<CmsPost>(res) ?? null;
        } catch (err: any) {
            if (err?.response?.status === 404) return null;
            throw err;
        }
    },

    /** A navigation menu for a placement, resolved for the current flavor. */
    getMenu: async (location: CmsMenuLocation, opts?: ReadOpts): Promise<CmsMenu> => {
        const flavor = opts?.flavor ?? getCmsFlavor();
        if (CMS_USE_MOCKS) return cmsMocks.getMenu(location, flavor);
        const query = buildQuery({ flavor });
        const res = await ftApiClient.get(`/cms/menus/${encodeURIComponent(location)}${query}`);
        return unwrap<CmsMenu>(res);
    },
};
